import { observer, use$ } from '@legendapp/state/react';
import { SAMPLE, statusMeta, oppMeta, depthMeta } from '../data/sample';
import { state$, openApp } from '../state';

export const FunctionDetail = observer(function FunctionDetail() {
  const funcId = use$(state$.funcId);
  const d = SAMPLE;
  const f = d.functions.find((x) => x.id === funcId) ?? d.functions[0]!;
  const st = statusMeta(f.status);
  const om = oppMeta(f.opp.score);
  const fi = d.functions.indexOf(f);

  const covering = d.apps
    .map((a) => ({ a, depth: a.depths[fi]!, score: a.scores[fi] }))
    .filter((x) => x.depth > 0)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  const reviews = [
    {
      stars: '★★★★★',
      app: covering[0] ? covering[0].a.name : '—',
      tag: 'Praise',
      fg: '#008060',
      bg: '#f1f8f5',
      border: '#a6d9c0',
      body: `"${f.name} is super smooth, exactly what my store needed. Up and running in 5 minutes."`,
    },
    {
      stars: '★★★☆☆',
      app: covering[1] ? covering[1].a.name : '—',
      tag: 'Complaint',
      fg: '#d72c0d',
      bg: '#fdeae7',
      border: '#f4c4ba',
      body: `"${f.name} still lacks customization; going advanced means a much pricier plan."`,
    },
    {
      stars: '★★★★☆',
      app: covering[2] ? covering[2].a.name : covering[0] ? covering[0].a.name : '—',
      tag: 'Request',
      fg: '#b98900',
      bg: '#fff8e8',
      border: '#e8d28a',
      body: `"Wish there were AI to auto-suggest content for ${f.name.toLowerCase()}; doing it by hand is tiring."`,
    },
  ];

  const pricing = d.apps
    .filter((a) => a.gated[fi])
    .slice(0, 4)
    .map((a) => ({ app: a.name, tier: 'Pro', price: '$' + (15 + d.apps.indexOf(a) * 4) + '/mo' }));

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', animation: 'nl-fade .3s ease' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 20,
          flexWrap: 'wrap',
          marginBottom: 20,
        }}
      >
        <div style={{ maxWidth: 600 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>
              {f.name}
            </h1>
            <span
              style={{
                padding: '3px 10px',
                borderRadius: 7,
                background: st.bg,
                color: st.fg,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {st.label}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 13.5, color: '#616161', lineHeight: 1.55 }}>
            {f.definition}
          </p>
        </div>
        <div
          style={{
            textAlign: 'center',
            background: '#fff',
            border: '1px solid #e1e3e5',
            borderRadius: 13,
            padding: '14px 22px',
          }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono'",
              fontSize: 28,
              fontWeight: 700,
              color: om.fg,
              lineHeight: 1,
            }}
          >
            {f.opp.score}
          </div>
          <div
            style={{
              fontSize: 10.5,
              color: '#6d7175',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginTop: 4,
            }}
          >
            Opportunity
          </div>
        </div>
      </div>

      <div
        style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 18, alignItems: 'start' }}
      >
        {/* apps ranked */}
        <div
          style={{ background: '#fff', border: '1px solid #e1e3e5', borderRadius: 16, padding: 20 }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 4px' }}>
            Which apps do it — and how well
          </h2>
          <p style={{ margin: '0 0 16px', fontSize: 12.5, color: '#6d7175' }}>
            So you know the bar to beat. Ranked by quality score.
          </p>
          {covering.map((x, i) => {
            const dm = depthMeta(x.depth);
            return (
              <div
                key={x.a.id}
                onClick={() => openApp(x.a.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 13,
                  padding: '11px 0',
                  borderBottom: '1px solid #f1f1f1',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono'",
                    fontSize: 12,
                    color: '#b4b9c2',
                    fontWeight: 600,
                    width: 18,
                  }}
                >
                  {i + 1}
                </span>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    background: x.a.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: 12,
                    color: '#303030',
                  }}
                >
                  {x.a.initials}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>{x.a.name}</div>
                  <div style={{ fontSize: 11, color: '#a5abb5' }}>{dm.label}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: 160 }}>
                  <div
                    style={{
                      flex: 1,
                      height: 7,
                      borderRadius: 5,
                      background: '#ebebeb',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${x.score ?? 0}%`,
                        background: '#9aa0a6',
                        borderRadius: 5,
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono'",
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#5b626a',
                      width: 26,
                      textAlign: 'right',
                    }}
                  >
                    {x.score}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* reviews */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #e1e3e5',
              borderRadius: 16,
              padding: 20,
            }}
          >
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 14px' }}>
              Representative reviews
            </h2>
            {reviews.map((r, i) => (
              <div
                key={i}
                style={{
                  padding: '11px 13px',
                  borderRadius: 11,
                  background: r.bg,
                  marginBottom: 10,
                  border: `1px solid ${r.border}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: r.fg }}>{r.stars}</span>
                  <span style={{ fontSize: 11.5, color: '#6d7175', fontWeight: 600 }}>{r.app}</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: 10,
                      fontWeight: 700,
                      color: r.fg,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {r.tag}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 12.5, color: '#616161', lineHeight: 1.5 }}>
                  {r.body}
                </p>
              </div>
            ))}
          </div>
          {/* pricing evidence */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #e1e3e5',
              borderRadius: 16,
              padding: 20,
            }}
          >
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 4px' }}>Pricing evidence</h2>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#6d7175' }}>
              Which apps gate this function behind a paywall.
            </p>
            {pricing.map((p, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 0',
                  borderBottom: '1px solid #f1f1f1',
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono'",
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#fff',
                    background: '#2c6ecb',
                    borderRadius: 5,
                    padding: '2px 6px',
                  }}
                >
                  Pricing
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{p.app}</span>
                <span style={{ fontSize: 12, color: '#616161' }}>
                  {p.tier} · <b style={{ fontFamily: "'JetBrains Mono'" }}>{p.price}</b>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});
